import type { BmiCalculatorState, BmiImperialHeightMode, BmiImperialWeightMode, BmiUnitSystem } from "../types";

interface BmiFormProps {
  state: BmiCalculatorState;
  onFieldChange: (field: keyof BmiCalculatorState, value: string) => void;
  onImperialHeightModeChange: (mode: BmiImperialHeightMode) => void;
  onImperialWeightModeChange: (mode: BmiImperialWeightMode) => void;
  onUnitSystemChange: (unitSystem: BmiUnitSystem) => void;
}

export function BmiForm({
  state,
  onFieldChange,
  onImperialHeightModeChange,
  onImperialWeightModeChange,
  onUnitSystemChange
}: BmiFormProps) {
  return (
    <section className="bmi-panel bmi-form-panel" aria-label="BMI calculator inputs">
      <div className="bmi-segment" aria-label="Measurement units">
        <button
          className={state.unitSystem === "metric" ? "selected" : ""}
          type="button"
          aria-pressed={state.unitSystem === "metric"}
          onClick={() => onUnitSystemChange("metric")}
        >
          Metric
        </button>
        <button
          className={state.unitSystem === "imperial" ? "selected" : ""}
          type="button"
          aria-pressed={state.unitSystem === "imperial"}
          onClick={() => onUnitSystemChange("imperial")}
        >
          Imperial
        </button>
      </div>

      {state.unitSystem === "metric" ? (
        <div className="bmi-fields">
          <label className="bmi-field">
            <span>Height</span>
            <div>
              <input
                inputMode="numeric"
                placeholder="175"
                type="text"
                value={state.heightCentimetres}
                onChange={(event) => onFieldChange("heightCentimetres", event.target.value)}
              />
              <strong>cm</strong>
            </div>
          </label>
          <label className="bmi-field">
            <span>Weight</span>
            <div>
              <input
                inputMode="numeric"
                placeholder="72"
                type="text"
                value={state.weightKilograms}
                onChange={(event) => onFieldChange("weightKilograms", event.target.value)}
              />
              <strong>kg</strong>
            </div>
          </label>
        </div>
      ) : (
        <div className="bmi-fields">
          <div className="bmi-field">
            <div className="bmi-field-heading">
              <span>Height</span>
              <div className="bmi-mini-segment" aria-label="Imperial height format">
                <button
                  className={state.imperialHeightMode === "feet-inches" ? "selected" : ""}
                  type="button"
                  aria-pressed={state.imperialHeightMode === "feet-inches"}
                  onClick={() => onImperialHeightModeChange("feet-inches")}
                >
                  ft/in
                </button>
                <button
                  className={state.imperialHeightMode === "inches" ? "selected" : ""}
                  type="button"
                  aria-pressed={state.imperialHeightMode === "inches"}
                  onClick={() => onImperialHeightModeChange("inches")}
                >
                  inches
                </button>
              </div>
            </div>
            {state.imperialHeightMode === "feet-inches" ? (
              <div className="bmi-height-pair">
                <input
                  aria-label="Height feet"
                  inputMode="numeric"
                  placeholder="5"
                  type="text"
                  value={state.heightFeet}
                  onChange={(event) => onFieldChange("heightFeet", event.target.value)}
                />
                <strong>ft</strong>
                <input
                  aria-label="Height inches"
                  inputMode="numeric"
                  placeholder="10"
                  type="text"
                  value={state.heightInches}
                  onChange={(event) => onFieldChange("heightInches", event.target.value)}
                />
                <strong>in</strong>
              </div>
            ) : (
              <div>
                <input
                  aria-label="Height inches"
                  inputMode="numeric"
                  placeholder="70"
                  type="text"
                  value={state.heightTotalInches}
                  onChange={(event) => onFieldChange("heightTotalInches", event.target.value)}
                />
                <strong>in</strong>
              </div>
            )}
          </div>
          <div className="bmi-field">
            <div className="bmi-field-heading">
              <span>Weight</span>
              <div className="bmi-mini-segment" aria-label="Imperial weight format">
                <button
                  className={state.imperialWeightMode === "pounds" ? "selected" : ""}
                  type="button"
                  aria-pressed={state.imperialWeightMode === "pounds"}
                  onClick={() => onImperialWeightModeChange("pounds")}
                >
                  lb
                </button>
                <button
                  className={state.imperialWeightMode === "stones-pounds" ? "selected" : ""}
                  type="button"
                  aria-pressed={state.imperialWeightMode === "stones-pounds"}
                  onClick={() => onImperialWeightModeChange("stones-pounds")}
                >
                  st/lb
                </button>
              </div>
            </div>
            {state.imperialWeightMode === "pounds" ? (
              <div>
                <input
                  aria-label="Weight pounds"
                  inputMode="numeric"
                  placeholder="160"
                  type="text"
                  value={state.weightPounds}
                  onChange={(event) => onFieldChange("weightPounds", event.target.value)}
                />
                <strong>lb</strong>
              </div>
            ) : (
              <div className="bmi-weight-pair">
                <input
                  aria-label="Weight stones"
                  inputMode="numeric"
                  placeholder="11"
                  type="text"
                  value={state.weightStones}
                  onChange={(event) => onFieldChange("weightStones", event.target.value)}
                />
                <strong>st</strong>
                <input
                  aria-label="Weight pounds"
                  inputMode="numeric"
                  placeholder="6"
                  type="text"
                  value={state.weightStonePounds}
                  onChange={(event) => onFieldChange("weightStonePounds", event.target.value)}
                />
                <strong>lb</strong>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
